import {resolveBackground} from '#core/utils/color-utils.js';
import {icon} from '#core/utils/style-utils.js';
import {merge} from '#core/utils/config-utils.js';

import {ApplicationConfig} from '#core/config/application-config.js';

import {generatePivotButton} from '#core/application/modules/tiles/pivot/index.js';
import {
    generateErrorNotification,
    generateSuccessNotification,
    generateWarningNotification,
} from '#core/application/modules/tiles/notification/index.js';
import {generateSkeletonPart} from '#core/application/modules/tiles/skeleton-part/index.js';
import {generateAnimatedButton, generateSecondaryButton} from '#core/application/modules/tiles/button/index.js';
import {generateEventTimingStatus} from '#core/application/modules/tiles/event-timing-status/index.js';
import {generateMiniScoreboard} from '#core/application/modules/tiles/mini-scoreboard/index.js';
import {generateBetBaseInfo} from '#core/application/modules/tiles/bet-base-info/index.js';
import {generateDotNotification} from '#core/application/modules/tiles/dot-notification/index.js';
import {generateBetStatusIcon} from '#core/application/modules/tiles/bet-status-icon/index.js';

export function yourBetsFactory(PALETTE) {
    const {FONT} = PALETTE;

    const OPEN_BET_CONTAINER_STYLE = {
        style: {
            ...resolveBackground(PALETTE.OPEN_BET_CONTAINER_BG),
            borderRadius: PALETTE.OPEN_BET_CONTAINER_BORDER_RADIUS,
            boxShadow: PALETTE.OPEN_BET_CONTAINER_SHADOW,
            _desktop_maxWidth: 288, // this is AppViewSportsbook.sideMenuContainer.style.width - OpenBetsView.betContainer.style.padding
            maxWidth: '100%',
        },
    };
    const OPEN_BET_SKELETON_PART_STYLE = generateSkeletonPart(PALETTE.OPEN_BET_SKELETON_PART);
    const OPEN_BET_BASE_INFO = generateBetBaseInfo(PALETTE.OPEN_BET_BASE_INFO);

    return {
        OpenBetsView: {
            container: {
                style: {
                    ...resolveBackground(PALETTE.YOUR_BETS_CONTAINER_BG),
                    fontSize: FONT.SIZE_MEDIUM,
                },
            },
            tabs: {
                container: {
                    style: {
                        padding: '0 0 8px',
                    },
                },
                tabButtons: {
                    container: {
                        style: {
                            padding: '0 0 8px',
                        },
                    },
                    selectableButton: generatePivotButton(PALETTE.YOUR_BETS_TAB_SELECTOR),
                },
            },
            loginInvitationContainer: {
                style: {
                    flexBasis: '112px',
                },
            },
            loginInvitationText: {
                style: {
                    ...PALETTE.YOUR_BETS_EMPTY_CONTENT_MESSAGE.EMPTY_CONTENT_MESSAGE_TEXT,
                },
            },
            emptyOpenBetsText: {
                container: {
                    padding: '27px 0',
                },
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    ...PALETTE.YOUR_BETS_EMPTY_CONTENT_MESSAGE.EMPTY_CONTENT_MESSAGE_TEXT,
                },
            },
            allBetsContainer: {
                style: {
                    padding: '4px 0 0 0',
                    margin: '8px 0 0',
                },
            },
            betHistoryButton: {
                ...(ApplicationConfig.isEmbedded
                    ? {
                          button: {
                              style: {
                                  base: {
                                      display: 'none',
                                  },
                              },
                          },
                      }
                    : {}),
                buttonLabel: {
                    style: PALETTE.OPEN_BET_HISTORY_LINK.SECONDARY_LINK_TEXT,
                },
                buttonIcon: {
                    style: {
                        fill: PALETTE.OPEN_BET_HISTORY_LINK.SECONDARY_LINK_ICON_COLOR,
                    },
                },
            },
        },
        // your bet
        YourBetView: {
            container: OPEN_BET_CONTAINER_STYLE,
            priceBoostIndicator: OPEN_BET_BASE_INFO.priceBoostValue,
            startingPriceIndicator: OPEN_BET_BASE_INFO.startingPriceIndicator,
            betInfoContainer: {
                style: {
                    fontSize: FONT.SIZE_SMALL,
                    padding: '0 0 8px 20px',
                },
            },
            labels: {
                style: {
                    minWidth: 0,
                },
            },
            // bet-funds info
            bonusIcon: OPEN_BET_BASE_INFO.betFundsInfo.medium.icon,
            bestOddsIcon: OPEN_BET_BASE_INFO.betFundsInfo.medium.icon,
            freeBetIcon: OPEN_BET_BASE_INFO.betFundsInfo.medium.icon,
            totalStakeInfo: {
                betInfo: OPEN_BET_BASE_INFO.betFundsInfo.medium,
            },
            totalPayoutInfo: {
                payout: {
                    betInfo: merge(OPEN_BET_BASE_INFO.betFundsInfo.medium, {
                        icon: {
                            style: {
                                minWidth: 16,
                                minHeight: 16,
                                _mobile_minHeight: '16px',
                                _mobile_minWidth: '16px',
                                _mobile_padding: '0 4px 0 0',
                            },
                        },
                    }),
                },
                payoutAfterTax: {
                    betInfo: merge(OPEN_BET_BASE_INFO.betFundsInfo.medium, {
                        icon: {
                            style: {
                                minWidth: 16,
                                minHeight: 16,
                            },
                        },
                    }),
                },
            },
            oddsBoostInfo: {
                betInfo: OPEN_BET_BASE_INFO.betFundsInfo.normal,
            },
            betHeaderText: {
                style: OPEN_BET_BASE_INFO.title,
            },
            dateText: {
                style: OPEN_BET_BASE_INFO.date,
            },
            legStatusIcon: OPEN_BET_BASE_INFO.betStatusIcon,
            selectionName: {
                style: OPEN_BET_BASE_INFO.selectionName,
            },
            odds: {
                style: OPEN_BET_BASE_INFO.odds,
            },
            priceInfoContainer: {
                itemSpacing: 4,
            },
            oddsLabel: {
                style: OPEN_BET_BASE_INFO.odds,
            },
            betStatusContainer: {
                style: OPEN_BET_BASE_INFO.betStatus,
            },
            settledStatusIcon: generateBetStatusIcon(PALETTE.SETTLED_BET_STATUS_ICON),
            suspendedMarket: {
                container: {
                    ...OPEN_BET_BASE_INFO.suspendedNotification,
                    padding: '2px 4px',
                    fontSize: FONT.SIZE_SMALL,
                    fontWeight: FONT.WEIGHT_NORMAL,
                },
            },
            eventName: {
                style: OPEN_BET_BASE_INFO.eventName,
            },
            marketName: {
                style: OPEN_BET_BASE_INFO.marketName,
            },
            matchAccaEventName: {
                style: OPEN_BET_BASE_INFO.betBuilderMarketName,
            },
            matchAccaEventNameIndicator: {
                style: OPEN_BET_BASE_INFO.betBuilderMarketName,
            },
            // leg-info-row
            legInfoRow: {
                eventTimingStatus: merge(generateEventTimingStatus(PALETTE.OPEN_BET_LIVE_EVENT_TIMING_STATUS), {
                    liveText: {
                        style: {
                            fontSize: FONT.SIZE_SMALL,
                            fontWeight: FONT.WEIGHT_SEMI_BOLD,
                        },
                    },
                    dateTimeContainer: {
                        separator: {
                            style: {
                                color: PALETTE.OPEN_BET_LIVE_EVENT_DATA_SEPARATOR,
                            },
                        },
                    },
                }),
                miniScoreboard: merge(generateMiniScoreboard(PALETTE.OPEN_BET_LIVE_EVENT_SCOREBOARD), {
                    scoreColumn: {
                        margin: 0,
                    },
                    scoresText: {
                        padding: 0,
                    },
                    scoresTextMain: {
                        padding: 0,
                    },
                    scoreCount: {
                        style: {
                            goalContainer: {
                                right: 'unset',
                                left: 0,
                            },
                        },
                    },
                }),
                separator: {
                    container: {
                        alignSelf: 'center',
                    },
                    style: {
                        color: PALETTE.OPEN_BET_LIVE_EVENT_DATA_SEPARATOR,
                        fontSize: FONT.SIZE_SMALL,
                    },
                },
                rowText: {
                    style: {
                        fontSize: FONT.SIZE_SMALL,
                        fontWeight: FONT.WEIGHT_SEMI_BOLD,
                        lineHeight: 'initial',
                        ...PALETTE.OPEN_BET_RESULT_TEXT,
                    },
                },
            },
            earlyPayoutIndicator: OPEN_BET_BASE_INFO.earlyPayoutIndicator,
            totalOdds: OPEN_BET_BASE_INFO.betTotalOdds,
            oddsBoostNotification: OPEN_BET_BASE_INFO.oddsBoostLabel,
            matchAccaOddsBoostNotification: OPEN_BET_BASE_INFO.oddsBoostLabel,
            betShareAndCashoutContainer: {
                style: {
                    padding: '0 0 8px',
                },
            },
            cashoutNotificationsContainer: {
                style: {
                    padding: '0 8px 8px',
                },
            },
            shareBetContainer: {
                style: {
                    padding: FEATURES.betSharing ? '0 8px' : 0,
                },
            },
            shareBet: {
                shareButton: {
                    button: {
                        style: {
                            base: {
                                fontSize: FONT.SIZE_MEDIUM,
                                ...PALETTE.OPEN_BET_SHARE_BET_BUTTON.TERTIARY_BUTTON_TEXT,
                            },
                        },
                    },
                    buttonIcon: {
                        style: {
                            fill: PALETTE.OPEN_BET_SHARE_BET_BUTTON.TERTIARY_BUTTON_ICON_COLOR,
                        },
                    },
                },
            },
            // cash-out
            cashout: {
                container: {
                    style: {
                        padding: '0 8px',
                    },
                },
                cashoutButton: generateAnimatedButton(PALETTE.OPEN_BET_CASH_OUT_BUTTON),
                cancelButton: generateSecondaryButton(PALETTE.OPEN_BET_CASH_OUT_CANCEL_BUTTON),
                settingsButtonIcon: icon(PALETTE.OPEN_BET_CASH_OUT_SETTINGS_BUTTON.TERTIARY_BUTTON_ICON_COLOR),
                autoCashoutRuleDotNotification: generateDotNotification(
                    PALETTE.OPEN_BET_CASH_OUT_SETTINGS_BUTTON_DOT_NOTIFICATION
                ),
                errorNotification: generateErrorNotification(PALETTE.OPEN_BET_CASH_OUT_ERROR_NOTIFICATION),
                successNotification: generateSuccessNotification(PALETTE.OPEN_BET_CASH_OUT_SUCCESS_NOTIFICATION),
                warningNotification: generateWarningNotification(PALETTE.OPEN_BET_CASH_OUT_WARNING_NOTIFICATION),
            },
        },
        // skeleton
        OpenBetsSkeleton: {
            container: {
                style: {
                    padding: '4px 8px',
                },
            },
            tabLabel: OPEN_BET_SKELETON_PART_STYLE,
            betContainer: OPEN_BET_CONTAINER_STYLE,
            betTitlePlaceholder: OPEN_BET_SKELETON_PART_STYLE,
            betDatePlaceholder: OPEN_BET_SKELETON_PART_STYLE,
            betStatusPlaceholder: OPEN_BET_SKELETON_PART_STYLE,
            legStatusIconPlaceholder: OPEN_BET_SKELETON_PART_STYLE,
            selectionNamePlaceholder: OPEN_BET_SKELETON_PART_STYLE,
            marketNamePlaceholder: OPEN_BET_SKELETON_PART_STYLE,
            eventNamePlaceholder: OPEN_BET_SKELETON_PART_STYLE,
            eventDatePlaceholder: OPEN_BET_SKELETON_PART_STYLE,
            oddsPlaceholder: OPEN_BET_SKELETON_PART_STYLE,
            stakeTextPlaceholder: OPEN_BET_SKELETON_PART_STYLE,
            stakeValuePlaceholder: OPEN_BET_SKELETON_PART_STYLE,
            payoutTextPlaceholder: OPEN_BET_SKELETON_PART_STYLE,
            payoutValuePlaceholder: OPEN_BET_SKELETON_PART_STYLE,
        },
    };
}
